import React, { useEffect, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useInView } from 'react-intersection-observer'
import { useArtistsStore } from '../../../Storage/artistsPage/useArtistsStore'
import { useAPI } from '../../../Contexts/APIContext'
import ArtistGridItem from '../../../Components/ArtistProfileComponents/ArtistGridItem/ArtistGridItem'
import AddArtistButton from '../../../Components/IngestRequest/AddArtistButton'
import DebounceSearch from '../../../Components/GeneralComponents/DebounceSearch/DebounceSearch'
import Loader from '../../../Components/GeneralComponents/Loader'
import classes from './index.module.css'
import { useArtistSelection } from '../../../Helpers/hooks/useArtistSelection'

export default function AllArtists() {
  const pageLimit = 50
  const pageLimitSearch = 20
  const { getArtists, searchArtists } = useAPI()
  const [ref, inView] = useInView()
  const [refSearch, inViewSearch] = useInView()
  const { handleArtistSelection } = useArtistSelection()


  const searchTerm = useArtistsStore(state => state.searchTerm)
  const setSearchTerm = useArtistsStore(state => state.setSearchTerm)

  const inputValue = useArtistsStore(state => state.inputValue)
  const setInputValue = useArtistsStore(state => state.setInputValue)

  const {
    data,
    isError,
    error,
    isLoading,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ['artists'],
    queryFn: ({ pageParam = 1 }) => getArtists(pageParam, pageLimit),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.artists.length === pageLimit) {
        return pages.length + 1
      }
      return undefined
    },
  })

  const {
    data: dataSearch,
    isError: isErrorSearch,
    error: errorSearch,
    isLoading: isLoadingSearch,
    fetchNextPage: fetchNextPageSearch,
    isFetching: isFetchingSearch,
    isFetchingNextPage: isFetchingNextPageSearch,
    hasNextPage: hasNextPageSearch,
  } = useInfiniteQuery({
    queryKey: ['artists_search', searchTerm],
    queryFn: ({ pageParam = 1 }) =>
      searchArtists(searchTerm, pageParam, pageLimitSearch),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.artists.length === pageLimitSearch) {
        return pages.length + 1
      }
      return undefined
    },
    enabled: !!searchTerm,
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetching) {
      fetchNextPage()
    }
  }, [inView])

  useEffect(() => {
    if (inViewSearch && hasNextPageSearch && !isFetchingSearch) {
      fetchNextPageSearch()
    }
  }, [inViewSearch])

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <h1 className={classes.title}>Artists</h1>
        <div className={classes.searchGroup}>
          <AddArtistButton />
          <div className={classes.searchWrapper}>
            <DebounceSearch
              inputValue={inputValue}
              setInputValue={setInputValue}
              searchValue={searchTerm}
              setSearchValue={setSearchTerm}
              placeholder={'Search Artists...'}
            />
          </div>
        </div>
      </div>

      {!isError && !isErrorSearch && !searchTerm && data && (
        <div className={classes.artists}>
          {data.pages.map((page, index) => (
            <React.Fragment key={index}>
              {page.artists.map((artist, innerIndex) => (
                <ArtistGridItem
                  key={innerIndex}
                  artist={artist}
                  onClick={handleArtistSelection}
                />
              ))}
            </React.Fragment>
          ))}
          <div ref={ref}>{isFetching ? <Loader /> : null}</div>
        </div>
      )}

      {!isErrorSearch && searchTerm && dataSearch && (
        <div className={classes.artists}>
          {dataSearch.pages.map((page, index) => (
            <React.Fragment key={index}>
              {page.artists.map((artist, innerIndex) => (
                <ArtistGridItem
                  key={innerIndex}
                  artist={artist}
                  onClick={handleArtistSelection}
                />
              ))}
            </React.Fragment>
          ))}
          <div ref={refSearch}>{isFetchingSearch ? <Loader /> : null}</div>
        </div>
      )}

      {(isError || isErrorSearch) && (
        <div className={classes.artists}>
          <p>Error loading artists</p>
        </div>
      )}
    </div>
  )
}